import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Times from '~/bootcamp/Activities/components/Times'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { multiplyDecimals } from '~/bootcamp/Activities/Multiplication/LongMulti/helpers'

import { getRoundMultiDecimalTerms } from './util'

export default function RoundMultiDecimal({
  difficultyLevel,
  onNextQuestionClick,
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const [answer, setAnswer] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<Array<{ decimalNumber: number; roundNumber: number }>>([])
  const { t } = useTranslation(['bootcamp', 'generics'])

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const { decimalNumber, roundNumber } = useMemo(() => {
    if (!difficultyLevel) return { decimalNumber: 0, roundNumber: 0 } // default
    const previousDecimals = taskHistory.map((item) => item.decimalNumber)
    return getRoundMultiDecimalTerms(difficultyLevel, previousDecimals)
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    setAnswer('')
  }, [decimalNumber, roundNumber])

  useEmitInstance(
    onInstanceChange,
    decimalNumber && roundNumber ? `${decimalNumber} \\cdot ${roundNumber} = \\square` : null,
  )

  const handleOnCheckAnswerClick = (): void => {
    const correctAnswer = multiplyDecimals(decimalNumber, roundNumber)
    const isCorrectAnswer = correctAnswer === parseFloat(answer)
    onAnswerStateChange(isCorrectAnswer)
  }

  const handleOnNextQuestionClick = (): void => {
    onAnswerStateChange(null)
    setAnswer('')

    setTaskHistory((prevState) => {
      return [{ decimalNumber, roundNumber }, ...prevState].slice(0, 8)
    })

    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper className="py-5">
      <MultiplicationActivityWrapper>
        <div>
          <span>{decimalNumber?.toString().replace('.', t('bootcamp:signs.decimal'))}</span>
          <Times />
          <span>{roundNumber}</span>
        </div>
        <div>
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertProduct')}
            autoFocus={autofocus}
            isError={answerState === false}
            isWide
            key={inputKey}
            maxDigits={15}
            onChange={(value) => {
              setAnswer(value)
              onAnswerStateChange(null)
            }}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </div>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}
